import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';
import locales from '../../../content.json';
import s from './index.module.scss';

import CalculateOfferPrices from '../../../utils/scripts/calculateOfferPrices';
import { calculatePrice } from '../../../utils/scripts/calculatePrice';

import Option from '../Option';
import PromoCode from '../../../components/common/promoCode';

import { getPricesConfig } from '../../getPricesConfig';
import { DEFAULT_DISCOUNT } from '../../discountConfig';
import { RootState } from '../../../store';
import { PAYMENT_TARIFF } from '../../../types';
import { getCurrencySymbol } from '../../../utils/scripts/currency';
import {
  OfferProps,
  adjustDayPrice,
  formatOptionTitle,
  priceFirstPart,
  priceSecondPart,
  useDayPrices,
  useOfferProducts,
  useSelectProduct,
} from '../offerShared';

/** Discount tariff that gets the special-offer styling on this design (v43/v44 paywall). */
const SPECIAL_OFFER_TARIFFS = [PAYMENT_TARIFF.V44];

const OfferV4 = ({ currentProduct, secondScreenWrapper }: OfferProps) => {
  const tariff = useSelector((state: RootState) => state.data.tariff);

  const { firstPriceConfig, secondPriceConfig, thirdPriceConfig } =
    getPricesConfig(tariff).everydoggyNewDesign;

  const currency = getCurrencySymbol();
  const prices = useOfferProducts(firstPriceConfig, secondPriceConfig, thirdPriceConfig, tariff);
  const doOnClick = useSelectProduct();

  const specialOffer = SPECIAL_OFFER_TARIFFS.includes(tariff);

  const firstPrice = useMemo(() => {
    const { firstOption } = prices;
    const { defaultOldPricesForPeriod } = firstPriceConfig;

    return CalculateOfferPrices.setPriceFullV2({
      product: firstOption,
      defaultPrice: defaultOldPricesForPeriod,
      calculateFromIntro: false,
    });
  }, [prices, firstPriceConfig]);

  const secondPrice = useMemo(() => {
    const { secondOption } = prices;
    const { defaultOldPricesForPeriod } = secondPriceConfig;

    return calculatePrice(secondOption, {
      target: 'perMonth',
      source: 'auto',
      calculateFromIntro: true,
      defaultPrice: defaultOldPricesForPeriod,
    });
  }, [prices, secondPriceConfig]);

  const thirdPrice = useMemo(() => {
    const { thirdOption } = prices;
    const { defaultOldPricesForPeriod } = thirdPriceConfig;

    return calculatePrice(thirdOption, {
      target: 'per3Month',
      source: 'auto',
      calculateFromIntro: true,
      defaultPrice: defaultOldPricesForPeriod,
    });
  }, [prices, thirdPriceConfig]);

  const { firstPriceInDay, secondPriceInDay, thirdPriceInDay } = useDayPrices(
    prices,
    firstPriceConfig,
    secondPriceConfig,
    thirdPriceConfig
  );

  const secondOldPriceInWeek = secondPriceConfig?.defaultOldPricesForPeriod || '39.99';
  const thirdOldPriceInWeek = thirdPriceConfig?.defaultOldPricesForPeriod || '59.99';

  return (
    <div
      className={`
        ${s.optionsWrapper}
        ${s.optionStyleV2}
        ${specialOffer ? s.tariffV5 : ''}
        ${secondScreenWrapper ? s.secondScreenWrapper : ''}
      `}
    >
      <h2 className={s.optionsTitle}>{locales[window.userLang].tariff_title_choose}</h2>
      <PromoCode tariff={tariff} secondScreenWrapper={secondScreenWrapper} />
      <div className={s.options}>
        <Option
          className={s.option}
          selectedClassName={s.option__selected}
          currentProduct={currentProduct}
          optionValue={prices.firstOption?.id}
          productName={firstPriceConfig.productName}
          doOnClick={(val, pr) => doOnClick(val, pr, firstPriceConfig.defaultOldPrices)}
        >
          <div className={s.radio}></div>
          <div className={s.priceWrapepr}>
            <p className={s.priceWrapeprTitle}>{formatOptionTitle(firstPriceConfig.titleWeek)}</p>
            <p className={s.price}>
              {currency}
              {`${firstPrice}`}
            </p>
          </div>

          <div className={s.priceDay}>
            <div className={s.priceDayWrapper}>
              <p className={s.dollar}>{currency}</p>
              <div className={s.firstPart}>
                {priceFirstPart(firstPriceInDay)}.{priceSecondPart(firstPriceInDay)}
              </div>
            </div>
            <div className={s.priceDayText}>{firstPriceConfig.perDay}</div>
          </div>
        </Option>

        <Option
          className={s.option}
          selectedClassName={s.option__selected}
          currentProduct={currentProduct}
          optionValue={prices.secondOption?.id}
          productName={secondPriceConfig.productName}
          doOnClick={(val, pr) => doOnClick(val, pr, secondPriceConfig.defaultOldPrices)}
        >
          <div className={s.label}>{locales[window.userLang].payment_text_226}</div>
          <div className={s.bestOffer}>{specialOffer ? 70 : DEFAULT_DISCOUNT}% off</div>

          <div className={s.radio}></div>
          <div className={s.priceWrapepr}>
            <p className={s.priceWrapeprTitle}>{formatOptionTitle(secondPriceConfig.titleWeek)}</p>
            <p className={s.price}>
              <span className={s.lineRed}>
                {currency}
                {secondOldPriceInWeek}
              </span>{' '}
              {currency}
              {secondPrice}
            </p>
          </div>

          <div className={s.priceDay}>
            <div className={s.priceDayWrapper}>
              <p className={s.dollar}>{currency}</p>
              <div className={s.firstPart}>
                {priceFirstPart(secondPriceInDay)}.{priceSecondPart(secondPriceInDay)}
              </div>
            </div>
            <div className={s.priceDayText}>{firstPriceConfig.perDay}</div>
          </div>
        </Option>

        <Option
          className={s.option}
          selectedClassName={s.option__selected}
          currentProduct={currentProduct}
          optionValue={prices.thirdOption?.id}
          productName={thirdPriceConfig.productName}
          doOnClick={(val, pr) => doOnClick(val, pr, thirdPriceConfig.defaultOldPrices)}
        >
          <div className={s.radio}></div>
          <div className={s.priceWrapepr}>
            <p className={s.priceWrapeprTitle}>{formatOptionTitle(thirdPriceConfig.titleWeek)}</p>
            <p className={s.price}>
              <span className={s.lineRed}>
                {currency}
                {thirdOldPriceInWeek}
              </span>{' '}
              {currency}
              {thirdPrice}
            </p>
          </div>

          <div className={s.priceDay}>
            <div className={s.priceDayWrapper}>
              <p className={s.dollar}>{currency}</p>
              <div className={s.firstPart}>
                {priceFirstPart(adjustDayPrice(thirdPriceInDay))}.{priceSecondPart(adjustDayPrice(thirdPriceInDay))}
              </div>
            </div>
            <div className={s.priceDayText}>{firstPriceConfig.perDay}</div>
          </div>
        </Option>
      </div>
    </div>
  );
};

export default OfferV4;
